import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { PlayerGameScoreScore } from '@sorare/core/src/components/scoring/PlayerGameScoreScore';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { breakpoints, tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { isGameStarted } from 'shared/game/lib';
import { PrimaryBuyForAPlayer } from 'shared/player/PrimaryBuyForAPlayer';
import { BriefGame } from 'shared/stats/BriefGame';
import { BriefPlayerScore } from 'shared/stats/BriefPlayerScore';
import { PlayerGameScoreStats } from 'shared/stats/PlayerGameScoreStats';

import type {
  PlayerGameScoreDialogQuery,
  PlayerGameScoreDialogQueryVariables,
} from './__generated__/index.graphql';

const PLAYER_GAME_SCORE_DIALOG_QUERY = gql`
  query PlayerGameScoreDialogQuery($id: ID!) {
    so5 {
      playerGameScore(id: $id) {
        id
        scoreStatus
        anyPlayer {
          slug
          avatarPictureUrl
          ...BriefPlayerScore_anyPlayerInterface
        }
        anyGame {
          id
          ...BriefGame_anyGameInterface
        }
        anyPlayerGameStats {
          id
          anyTeam {
            slug
            ...BriefGame_teamInterface
          }
        }
        ...BriefPlayerScore_playerGameScoreInterface
        ...PlayerGameScoreScore_playerGameScoreInterface
        ...PlayerGameScoreStats_playerGameScoreInterface
      }
    }
  }
  ${BriefGame.fragments.anyGameInterface}
  ${BriefGame.fragments.teamInterface}
  ${BriefPlayerScore.fragments.anyPlayerInterface}
  ${BriefPlayerScore.fragments.playerGameScoreInterface}
  ${PlayerGameScoreScore.fragments.playerGameScoreInterface}
  ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
` as TypedDocumentNode<
  PlayerGameScoreDialogQuery,
  PlayerGameScoreDialogQueryVariables
>;

const Root = styled(Vertical)`
  --container-padding: var(--double-unit);
  padding: var(--container-padding);
  @media ${tabletAndAbove} {
    min-width: ${breakpoints.mobile}px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  playerGameScoreId: string;
  showUpsell?: boolean;
  inDrawer?: boolean;
  disableLinks?: boolean;
};

export const PlayerGameScoreDialog = ({
  onClose,
  open,
  playerGameScoreId,
  showUpsell,
  inDrawer,
  disableLinks,
}: Props) => {
  const openDialog = useOpenDialog();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(PLAYER_GAME_SCORE_DIALOG_QUERY, {
    variables: { id: idFromObject(playerGameScoreId)! },
    skip: !open,
  });
  const { playerGameScore } = data?.so5 || {};

  const onClick =
    sport === Sport.FOOTBALL && !inDrawer && playerGameScore && !disableLinks
      ? () => openDialog(DialogKey.game, playerGameScore.anyGame.id)
      : undefined;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={
        <Horizontal center>
          <FormattedMessage {...fantasy.playerPerformance} />
        </Horizontal>
      }
    >
      <>
        {!!loading && <LoadingIndicator />}
        {!!playerGameScore && (
          <Root gap={5}>
            <BriefPlayerScore
              pictureUrl={playerGameScore.anyPlayer.avatarPictureUrl}
              anyPlayer={playerGameScore.anyPlayer}
              playerGameScores={[playerGameScore]}
              disableLinks={disableLinks}
            >
              <PlayerGameScoreScore playerGameScore={playerGameScore} />
            </BriefPlayerScore>
            <BriefGame
              game={playerGameScore.anyGame}
              team={playerGameScore.anyPlayerGameStats.anyTeam}
              onClick={onClick}
            />
            {!!showUpsell && (
              <PrimaryBuyForAPlayer
                playerSlug={playerGameScore.anyPlayer.slug}
              />
            )}
            {playerGameScore.scoreStatus !== ScoreStatus.DID_NOT_PLAY &&
              isGameStarted(playerGameScore.anyGame.status) && (
                <PlayerGameScoreStats playerGameScores={[playerGameScore]} />
              )}
          </Root>
        )}
      </>
    </Dialog>
  );
};
